import { apiClient } from '@api'

/**
 * Feature Service
 *
 * Service layer for managing package features
 * Provides methods for CRUD operations on features
 */
class FeatureService {
	/**
	 * Get all features
	 * @param {Object} params - Query parameters (page, limit, status, category, search)
	 * @returns {Promise<Object>} Features data with pagination meta
	 */
	async getAll(params = {}) {
		const queryParams = new URLSearchParams()

		if (params.page) queryParams.append('page', params.page)
		if (params.limit) queryParams.append('limit', params.limit)
		if (params.status) queryParams.append('status', params.status)
		if (params.category) queryParams.append('category', params.category)
		if (params.search) queryParams.append('search', params.search)

		const queryString = queryParams.toString()
		const endpoint = queryString ? `/features?${queryString}` : '/features'

		return await apiClient.get(endpoint)
	}

	/**
	 * Get feature by ID
	 * @param {string|number} id - Feature ID
	 * @returns {Promise<Object>} Feature data
	 */
	async getById(id) {
		return await apiClient.get(`/features/${id}`)
	}

	/**
	 * Get feature by slug
	 * @param {string} slug - Feature slug
	 * @returns {Promise<Object>} Feature data
	 */
	async getBySlug(slug) {
		return await apiClient.get(`/features/slug/${slug}`)
	}

	/**
	 * Create new feature
	 * @param {Object} featureData - Feature data
	 * @returns {Promise<Object>} Created feature data
	 */
	async create(featureData) {
		return await apiClient.post('/features', featureData)
	}

	/**
	 * Update feature
	 * @param {string|number} id - Feature ID
	 * @param {Object} featureData - Updated feature data
	 * @returns {Promise<Object>} Updated feature data
	 */
	async update(id, featureData) {
		return await apiClient.put(`/features/${id}`, featureData)
	}

	/**
	 * Archive feature (change status to archived)
	 * @param {string|number} id - Feature ID
	 * @returns {Promise<Object>} Success response
	 */
	async archive(id) {
		return await apiClient.put(`/features/${id}`, { status: 'archived' })
	}

	/**
	 * Delete feature (soft delete)
	 * @param {string|number} id - Feature ID
	 * @returns {Promise<Object>} Success response
	 */
	async delete(id) {
		return await apiClient.delete(`/features/${id}`)
	}

	/**
	 * Restore archived feature
	 * @param {string|number} id - Feature ID
	 * @returns {Promise<Object>} Restored feature data
	 */
	async restore(id) {
		return await apiClient.post(`/features/${id}/restore`, {})
	}

	/**
	 * Get active features only
	 * @returns {Promise<Object>} Active features data
	 */
	async getActive() {
		return await apiClient.get('/features?status=active')
	}

	/**
	 * Get features by category
	 * @param {string} category - Category name
	 * @returns {Promise<Object>} Features data
	 */
	async getByCategory(category) {
		return await apiClient.get(`/features?category=${category}&status=active`)
	}

	/**
	 * Get all categories
	 * @returns {Promise<Object>} Categories data
	 */
	async getCategories() {
		return await apiClient.get('/features/categories')
	}

	/**
	 * Get features by IDs (batch)
	 * @param {Array<number>} ids - Array of feature IDs
	 * @returns {Promise<Object>} Features data
	 */
	async getByIds(ids) {
		return await apiClient.post('/features/batch', { ids })
	}
}

// Export singleton instance
export const featureService = new FeatureService()
export default featureService

